import type { Notification } from '@doneverse/shared'
import type { LucideIcon } from 'lucide-react'
import { ClipboardList, MessageCircle, MessageSquarePlus } from 'lucide-react'
import type { ReactNode } from 'react'
import businessBriefIcon from '@/assets/brand/icon-business-brief.png'
import blueprintIcon from '@/assets/brand/icon-roadmap-blueprint.png'

export const PLAYBOOK_NOTIFICATION_TYPES = new Set([
  'playbook_briefed',
  'playbook_brief_edited',
  'playbook_output_submitted',
  'playbook_output_edited',
  'playbook_feedback_submitted',
  'revision_requested',
])

/** Parses **bold** markers in notification body text into <strong> elements */
export function renderFormattedBody(text: string): ReactNode {
  const parts = text.split(/(\*\*.*?\*\*)/g)
  if (parts.length === 1) return text
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    return part
  })
}

export function getNotificationImage(
  notification: Notification,
  playbookThumbnailMap: Map<string, string | null>,
): string | null {
  if (PLAYBOOK_NOTIFICATION_TYPES.has(notification.type)) {
    return notification.reference_id ? (playbookThumbnailMap.get(notification.reference_id) ?? null) : null
  }
  switch (notification.type) {
    case 'business_brief_updated':
      return businessBriefIcon
    case 'project_created':
    case 'project_status_changed':
      return blueprintIcon
    default:
      return null
  }
}

const TASK_NOTIFICATION_TYPES = new Set([
  'task_created',
  'task_assigned',
  'task_status_changed',
  'task_comment_added',
])

export function getNotificationIcon(type: string): LucideIcon | null {
  if (type === 'new_message' || type === 'group_chat_added') return MessageCircle
  if (type === 'feedback_replied') return MessageSquarePlus
  if (TASK_NOTIFICATION_TYPES.has(type)) return ClipboardList
  return null
}

export type NotificationNavTarget =
  | { to: '/playbooks/$slug'; params: { slug: string } }
  | { to: '/business-brief' }
  | { to: '/messages/$contactId'; params: { contactId: string } }
  | { to: '/messages' }
  | { to: '/doing-blueprint' }
  | { to: '/tasks/$taskId'; params: { taskId: string } }
  | { to: '/feedback/$feedbackId'; params: { feedbackId: string } }
  | { to: '/feedback' }

/**
 * Resolves the destination route for a notification.
 * Playbook notifications store the playbook UUID as reference_id,
 * but routes use slugs — pass playbookSlugMap to resolve them.
 *
 * Returns a TanStack Router nav descriptor (path template + params),
 * not a resolved URL — dynamic routes need the template to match.
 */
export function getNotificationRoute(
  notification: Notification,
  playbookSlugMap?: Map<string, string>,
): NotificationNavTarget | null {
  switch (notification.type) {
    case 'playbook_briefed':
    case 'playbook_brief_edited':
    case 'playbook_output_submitted':
    case 'playbook_output_edited':
    case 'playbook_feedback_submitted':
    case 'revision_requested': {
      if (!notification.reference_id) return null
      const slug = playbookSlugMap?.get(notification.reference_id)
      return slug ? { to: '/playbooks/$slug', params: { slug } } : null
    }
    case 'business_brief_updated':
      return { to: '/business-brief' }
    case 'new_message':
    case 'group_chat_added':
      return notification.reference_id
        ? { to: '/messages/$contactId', params: { contactId: notification.reference_id } }
        : { to: '/messages' }
    case 'project_created':
    case 'project_status_changed': {
      // Project notifications about a specific playbook (e.g. a Doing Team
      // submitting completed work) reference the workspace_playbook so we can
      // deep-link to that playbook. Fall back to the Blueprint when the
      // reference can't be resolved (e.g. Doing-Team-facing notifications whose
      // playbook isn't in the viewer's workspace).
      const slug = notification.reference_id ? playbookSlugMap?.get(notification.reference_id) : undefined
      return slug ? { to: '/playbooks/$slug', params: { slug } } : { to: '/doing-blueprint' }
    }
    case 'task_created':
    case 'task_assigned':
    case 'task_status_changed':
    case 'task_comment_added':
      return notification.reference_id
        ? { to: '/tasks/$taskId', params: { taskId: notification.reference_id } }
        : null
    case 'feedback_replied':
      // Straight into the thread that was replied to, not the feedback list —
      // the user should land on the reply they were notified about.
      return notification.reference_id
        ? { to: '/feedback/$feedbackId', params: { feedbackId: notification.reference_id } }
        : { to: '/feedback' }
    default:
      return null
  }
}

export function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  const diffWeeks = Math.floor(diffDays / 7)

  if (diffMins < 1) return 'now'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}hr`
  if (diffDays < 14) return `${diffDays}d`
  return `${diffWeeks}w`
}

export function getNotificationActionLabel(type: string): string {
  if (PLAYBOOK_NOTIFICATION_TYPES.has(type)) return 'Go to Playbook'
  switch (type) {
    case 'business_brief_updated':
      return 'Go to Brief'
    case 'new_message':
    case 'group_chat_added':
      return 'Go to Messages'
    case 'project_created':
    case 'project_status_changed':
      return 'Go to Blueprint'
    case 'task_created':
    case 'task_assigned':
    case 'task_status_changed':
    case 'task_comment_added':
      return 'Go to Task'
    case 'feedback_replied':
      return 'Go to Feedback'
    default:
      return 'View'
  }
}
